
    import { Component, Inject, Mixins } from 'vue-property-decorator'
    import Layout from '../../common/components/layout/layout.vue'
    import OtpInput from '@/app/common/components/otp-input.vue'
    import LockoutOverlay from '@/app/settings/components/lockout-overlay.vue'
    import TwoFactorActionFooter from '@/app/settings/components/twofactor-action-footer.vue'
    import ResendCodeLink from '@/app/common/components/resend-code-link.vue'
    import { AddressStyleTranslationMixin } from '@/app/common/helpers/address-style-translation'
    import { TwoFactorLockoutMixin } from '@/app/common/helpers/twofactor-lockout.mixin'
    import { SendEmailOtpCommand } from '@/app/auth/commands/send-email-otp.command'
    import { DisableTwoFactorCommand } from '@/app/settings/commands/disable-two-factor.command'
    import { UpdateConsumerCommand } from '@/app/auth/commands/update-consumer.command'
    import { GetTwoFactorAuthStatusCommand } from '@/app/settings/commands/get-twofactor-auth-status.command'
    import { NotificationDispatcher } from '@/app/common/services/notification-dispatcher.service'
    import { TwoFactorRateLimitError } from '@/app/common/services/http/axios-client.service'
    import { mapState } from 'vuex'
    import { Consumer } from '@/app/identity'
    import {
        twoFactorSwitchStorage,
        TwoFactorMethod,
        TwoFactorStorage
    } from '@/app/auth/services/twofactor-storage.service'
    import { License } from '@/app/license/entities/license.entity'

    @Component({
        components: { Layout, OtpInput, LockoutOverlay, TwoFactorActionFooter, ResendCodeLink },
        mixins: [AddressStyleTranslationMixin],
        computed: {
            ...mapState('auth', ['consumer']),
            ...mapState('license', ['license'])
        }
    })
    export default class TwoFactorSwitch extends Mixins(TwoFactorLockoutMixin) {
        @Inject() sendEmailOtpCommand!: SendEmailOtpCommand
        @Inject() disableTwoFactorCommand!: DisableTwoFactorCommand
        @Inject() updateConsumerCommand!: UpdateConsumerCommand
        @Inject() getTwoFactorAuthStatusCommand!: GetTwoFactorAuthStatusCommand

        @Inject() notificationDispatcher!: NotificationDispatcher

        license!: License
        consumer!: Consumer

        loading = false
        emailSent = false
        maskedEmail = ''
        resending = false

        // Override storage getter from mixin
        get lockoutStorage(): TwoFactorStorage {
            return twoFactorSwitchStorage
        }

        get allowEmailTwoFactor(): boolean {
            return this.license?.features?.allowEmailTwoFactor ?? false
        }

        get currentMethod(): TwoFactorMethod {
            return (this.consumer?.twoFactorMethod as TwoFactorMethod) || 'totp'
        }

        get currentMethodLabel(): string {
            return this.currentMethod === 'totp'
                ? (this.$t('page.twofactor.method.app_title') as string)
                : (this.$t('page.twofactor.method.email') as string)
        }

        // Override endLockout to redirect to enabled 2FA page
        endLockout() {
            // Reset lockout state and check if we were actually locked out
            if (!this.resetLockoutState()) return

            // Use $nextTick to ensure Vue processes state changes before redirect
            this.$nextTick(() => {
                this.$router.push({ name: 'settings-twofactor' })
            })
        }

        // Override onRateLimitExpired to redirect instead of reload
        onRateLimitExpired() {
            // Always reset the rate limit state first (idempotent - safe to call multiple times)
            const wasRateLimited = this.rateLimited

            // Clear the rate limit state immediately
            this.rateLimited = false
            this.rateLimitMessage = ''
            this.attemptsRemaining = this.maxAttempts
            this.lockoutStorage.removeRateLimit()

            // Only clear the rate limit interval, NOT the lockout interval
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval)
                this.countdownInterval = null
            }

            // Only do redirect if we were actually rate limited
            if (!wasRateLimited) return

            // Use $nextTick to ensure Vue processes state changes before redirect
            this.$nextTick(() => {
                this.$router.push({ name: 'settings-twofactor' })
            })
        }

        async mounted() {
            // Guard: If 2FA method is TOTP (only option to switch to is Email)
            // but Email 2FA is not allowed, redirect back
            if (this.currentMethod === 'totp' && !this.allowEmailTwoFactor) {
                this.$router.replace({ name: 'settings-twofactor' })
                return
            }

            // Check lockout state: first sessionStorage, then database (using mixin method)
            await this.checkAndRestoreLockoutState(this.getTwoFactorAuthStatusCommand)

            // If current method is email, automatically send the verification code
            if (this.currentMethod === 'email' && !this.isLockedOut && !this.rateLimited) {
                this.sendEmailCode()
            } else if (this.currentMethod === 'totp' && !this.isLockedOut && !this.rateLimited) {
                // For TOTP, focus the first input immediately
                this.$nextTick(() => {
                    this.focusOtpInput()
                })
            }
        }

        async sendEmailCode(isResend = false) {
            // Prevent double clicks
            if (isResend && this.resending) return
            if (!isResend && this.loading) return

            try {
                if (isResend) {
                    this.resending = true
                } else {
                    this.loading = true
                }
                const result = await this.sendEmailOtpCommand.run(this.consumer.id)

                if (result.noEmail) {
                    await this.notificationDispatcher.error(result.message || 'No email address available')
                    this.goBack()
                    return
                }

                this.rateLimited = false
                this.maskedEmail = result.maskedEmail || ''
                this.emailSent = true
                this.resetOtpDigits()
                // Start cooldown timer after successful resend
                if (isResend) {
                    const resendCodeLink = this.$refs.resendCodeLink as ResendCodeLink
                    if (resendCodeLink) {
                        resendCodeLink.startCooldown()
                    }
                }
                // Focus the first OTP input after email is sent
                this.$nextTick(() => {
                    this.focusOtpInput()
                })
            } catch (err) {
                if (err instanceof TwoFactorRateLimitError) {
                    this.setRateLimit(err.retryAfterMinutes, err.message)
                    return
                }
                await this.notificationDispatcher.error((err as Error).message)
            } finally {
                this.loading = false
                this.resending = false
            }
        }

        async resendEmailCode() {
            await this.sendEmailCode(true)
        }

        goBack() {
            this.$router.push({ name: 'settings-twofactor' })
        }

        async verifyAndSwitch() {
            try {
                this.loading = true
                this.codeError = ''
                this.codeErrorActive = false

                // Verify and disable current 2FA method
                await this.disableTwoFactorCommand.run(this.confirmationCode, this.currentMethod)

                // Update consumer state
                await this.updateConsumerCommand.run()

                // After verification succeeds, redirect to setup new method
                this.$router.push({ name: 'settings-twofactor', query: { action: 'switch', from: this.currentMethod } })
            } catch (err) {
                // Check if this is a rate limit error from the backend
                if (err instanceof TwoFactorRateLimitError) {
                    // Backend has rate limited - use their lockout duration
                    this.lockoutDuration = err.retryAfterSeconds || err.retryAfterMinutes * 60
                    this.startLockout()
                    this.resetOtpDigits()
                    return
                }

                // Handle incorrect code using mixin method
                this.handleIncorrectCode()
            } finally {
                this.loading = false
            }
        }
    }
